import { useState } from "react";
import { getTrue } from "common";
import { displaySite } from "common/functions/sites";
import { Filter, type FilterDefault } from "common/types/filters";
import { Context } from "common/types/context";
import { Role } from "common/types/roles";
import { ActionButtonLarge } from "common/ui/buttons";
import { VerticalField } from "common/ui/field";
import { ActionItemButton } from "common/widgets/action-item";
import { StringInput } from "common/widgets/input-with-submit/string";
import { Required } from "common/widgets/required";
import { Selector } from "common/widgets/selector";
import { SiteSelector } from "common/widgets/site-selector";
import { createList } from "common/widgets/list";
import { ValueProps } from "common/with-value-for";
import {
  getAllDefaultCombinations,
  getAvailableRoles,
  getListItemsFromDefaults,
  getDefaultsFromListItems,
  getRoleLabel,
  getRolesForSelectedSites,
  getSitesToOmit,
} from "common/filter/form/defaults/functions";
import { getRoleOptionLabel } from "x/account-settings/users/functions";

import "./_index.scss";

interface PropTypes extends ValueProps<Filter> {
  context: Context;
  allRoles: Role[];
}

export interface FilterDefaultListItem {
  site: string;
  roleId: number;
}

const FilterDefaultsList = createList<FilterDefaultListItem>();

export const FilterDefaults = ({
  context,
  allRoles,
  value,
  onChange,
}: PropTypes) => {
  const { sites: allSites } = context;
  const { defaults = [], roleIds = [], sites = [] } = value;
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role>(undefined);

  const availableRoleIds = getAvailableRoles(allRoles, roleIds);

  const isAllowedToAdd = !!(selectedSites.length && selectedRole);

  const onChangeDefaults = (defaults: FilterDefault[]) => {
    onChange({
      ...value,
      defaults,
    });
  };

  const resetSelected = () => {
    setSelectedRole(undefined);
    setSelectedSites([]);
  };

  const onSelectAll = () => {
    onChangeDefaults(getAllDefaultCombinations(sites, availableRoleIds));
    resetSelected();
  };

  const onUnselectAll = () => {
    onChangeDefaults([]);
    resetSelected();
  };

  const onSelectedSitesChange = (sites: string[]) => {
    sites ? setSelectedSites(sites) : resetSelected();
  };

  const addNewDefault = () => {
    if (!isAllowedToAdd) return;

    const doesDefaultExistForSite = defaults.some(
      (d) => d.site === selectedSites[0],
    );

    const newDefaults = doesDefaultExistForSite
      ? defaults.map((def) =>
          def.site === selectedSites[0]
            ? { ...def, roleIds: [...def.roleIds, selectedRole?.id] }
            : def,
        )
      : [...defaults, { site: selectedSites[0], roleIds: [selectedRole.id] }];

    onChangeDefaults(newDefaults);
    resetSelected();
  };

  const renderItem = (filterDefault: FilterDefaultListItem) => (
    <div className="x-flex x-view-default-item">
      <StringInput
        className="x-flex-grow-1"
        disabled={true}
        value={displaySite(allSites)(filterDefault.site)}
        onChange={undefined}
      />
      <StringInput
        className="x-flex-grow-1"
        disabled={true}
        value={getRoleLabel(allRoles, filterDefault.roleId)}
        onChange={undefined}
      />
    </div>
  );

  const onListChange = (value: FilterDefaultListItem[]) =>
    onChangeDefaults(getDefaultsFromListItems(value));

  return (
    <>
      <div className="x-view-default-set-all">
        <ActionItemButton
          label={_("Select all")}
          icon={"fa fa-check-circle-o"}
          clickHandler={onSelectAll}
        />
        <ActionItemButton
          label={_("Deselect all")}
          clickHandler={onUnselectAll}
        />
      </div>
      <div className="x-view-default-header">
        <VerticalField
          label={_("Select Site")}
          input={
            <Required value={selectedSites} dataType="array">
              <SiteSelector
                context={context}
                placeholder={`${_("Select")}...`}
                allowClear={true}
                includeGroupSites={true}
                hideTreeSelector={true}
                sitesToOmit={getSitesToOmit(allSites, availableRoleIds, value)}
                value={selectedSites}
                onChange={onSelectedSitesChange}
              />
            </Required>
          }
        />
        <VerticalField
          label={_("Select Role")}
          input={
            <Required value={selectedRole}>
              <Selector<Role>
                className="x-role-selector"
                placeholder={`${_("Select")}...`}
                allowClear={true}
                options={getRolesForSelectedSites(
                  selectedSites,
                  allRoles,
                  value,
                )}
                getOptionLabel={getRoleOptionLabel}
                disabled={!selectedSites?.length}
                value={selectedRole}
                onChange={setSelectedRole}
              />
            </Required>
          }
        />
        <VerticalField
          input={
            <ActionButtonLarge
              disabled={!isAllowedToAdd}
              onClick={addNewDefault}
            >
              {_("Add")}
            </ActionButtonLarge>
          }
        />
      </div>
      <div className={"x-view-default-list"}>
        <FilterDefaultsList
          onDisplay={renderItem}
          canDelete={getTrue}
          value={getListItemsFromDefaults(defaults)}
          onChange={onListChange}
        />
      </div>
    </>
  );
};
